import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Users, MapPin, Building2 } from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal, { MaskedLine } from "@/components/Reveal";
import { services, IMAGES } from "@/data/content";

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-ink text-paper" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={IMAGES.hero} alt="Commercial construction site" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
      </motion.div>
      <div className="relative z-10 w-full px-6 md:px-12 pt-40 pb-14">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="text-xs md:text-sm uppercase tracking-[0.25em] text-paper/70 mb-8"
          data-testid="hero-eyebrow"
        >
          General Contractor — Charleston, South Carolina
        </motion.p>
        <h1 className="font-display font-medium uppercase leading-[0.92] tracking-tighter text-[12.5vw] md:text-[8.5vw] lg:text-[7vw]" data-testid="hero-headline">
          <MaskedLine delay={0.15}>No project too large.</MaskedLine>
          <MaskedLine delay={0.35}>
            No project <span className="text-accent">too small.</span>
          </MaskedLine>
        </h1>
        <div className="mt-10 pt-8 border-t border-paper/20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.8 }}
            className="md:col-span-5 text-paper/75 text-base md:text-lg leading-relaxed"
            data-testid="hero-subcopy"
          >
            A general contractor with 25+ years of experience and two divisions —
            commercial and residential — serving owners, developers, and homeowners
            across South Carolina.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="md:col-span-7 flex flex-wrap gap-4 md:justify-end"
          >
            <Link
              to="/request-quote"
              data-testid="hero-quote-button"
              className="group inline-flex items-center gap-3 bg-paper text-ink font-semibold uppercase tracking-widest text-sm px-8 py-4 hover:bg-accent hover:text-paper transition-colors"
            >
              Request a Quote
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/request-site-visit"
              data-testid="hero-visit-button"
              className="group inline-flex items-center gap-3 border border-paper/60 text-paper font-semibold uppercase tracking-widest text-sm px-8 py-4 hover:border-accent hover:text-accent transition-colors"
            >
              Book a Site Visit
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="px-6 md:px-12 py-24 md:py-36" data-testid="about-section">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">About us</p>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-9">
          <p className="text-ink text-lg md:text-2xl leading-relaxed max-w-3xl" data-testid="about-copy">
            Rogers American Construction is a family-owned general contractor based in
            Charleston, South Carolina. Our team brings more than 25 years of construction
            experience in the Charleston area, and many of our clients still know us by our
            former name — The Fielding Group. The name changed; the people, the standard,
            and the way we do business did not. As a family company, the person who prices
            your job is the person who answers for it, from the first site walk to the
            final walkthrough.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="border-t border-ink/10" data-testid="services-section">
      <div className="px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-4">Commercial Services</p>
            <h2 className="font-display font-medium uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95]">What we build</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-smoke text-base max-w-sm leading-relaxed">
              Upfits, new builds, renovations, and general construction for owners, developers, and facility managers.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-ink/10">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.06}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group relative flex flex-col justify-between border-b border-r border-ink/10 p-8 md:p-12 min-h-[380px] hover:bg-ink hover:text-paper transition-colors duration-300"
              >
                <div className="flex items-start justify-between">
                  <span className="font-display text-sm text-accent font-semibold">{s.num}</span>
                  <ArrowUpRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <div className="overflow-hidden my-8 h-40 md:h-48">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="h-full w-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                  />
                </div>
                <div>
                  <h3 className="font-display font-medium uppercase tracking-tight text-2xl md:text-3xl">{s.title}</h3>
                  <p className="mt-2 text-sm text-smoke group-hover:text-paper/60 transition-colors">{s.tag}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const trustItems = [
  { icon: Users, title: "Family owned & operated", copy: "A family business. The people who price your job are the people who answer for it." },
  { icon: ShieldCheck, title: "Licensed & Insured", copy: "Fully licensed and insured for construction work in South Carolina." },
  { icon: MapPin, title: "Charleston-based", copy: "Based in Charleston. Working across South Carolina." },
  { icon: Building2, title: "Any size project", copy: "Small repairs to ground-up builds. No project too large or small." },
];

function Trust() {
  return (
    <section className="bg-ink text-paper" data-testid="trust-section">
      <div className="px-6 md:px-12 py-20 md:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-4">Why us</p>
          <h2 className="font-display font-medium uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95] mb-14">
            A family business.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-paper/15">
          {trustItems.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.08} className="border-b border-r border-paper/15">
              <div className="p-8 md:p-10 min-h-[240px] flex flex-col justify-between" data-testid={`trust-item-${i}`}>
                <t.icon className="w-7 h-7 text-accent" strokeWidth={1.5} />
                <div className="mt-10">
                  <h3 className="font-display font-medium uppercase tracking-tight text-xl">{t.title}</h3>
                  <p className="mt-3 text-sm text-paper/60 leading-relaxed">{t.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatWeDo() {
  return (
    <section id="what-we-do" className="border-t border-ink/10 px-6 md:px-12 py-24 md:py-32" data-testid="what-we-do-section">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">What we do</p>
        </Reveal>
        <div className="md:col-span-9 space-y-8">
          <Reveal delay={0.1}>
            <p className="text-smoke text-base md:text-lg leading-relaxed max-w-3xl" data-testid="what-we-do-copy-1">
              We handle commercial upfits, renovations, and ground-up construction for office,
              retail, restaurant, medical, warehouse, and industrial properties — and no project
              is too large or too small. From moving a few walls to building an entire facility,
              our commercial division can take it on.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-smoke text-base md:text-lg leading-relaxed max-w-3xl" data-testid="what-we-do-copy-2">
              We also provide design-build services, working with owners and developers from the
              earliest sketches through permitting and construction — one contract, one team, one
              point of accountability. And our residential division brings the same approach to
              custom homes, renovations, and additions across the Charleston area.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Charleston() {
  return (
    <section className="border-t border-ink/10 px-6 md:px-12 py-20 md:py-32" data-testid="charleston-section">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-6">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-4">Where we work</p>
            <h2 className="font-display font-medium uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95] mb-8">
              Based in Charleston. Building across South Carolina.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-smoke text-base md:text-lg leading-relaxed mb-6">
              Downtown upfits, North Charleston industrial work, Mount Pleasant retail,
              and the wider Lowcountry.
            </p>
            <p className="text-smoke text-base md:text-lg leading-relaxed mb-10">
              And when a client needs us beyond the coast, we travel — Columbia, Greenville,
              Myrtle Beach, and everywhere in between.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/request-site-visit"
              data-testid="charleston-visit-button"
              className="group inline-flex items-center gap-3 border border-ink text-ink font-semibold uppercase tracking-widest text-sm px-8 py-4 hover:bg-ink hover:text-paper transition-colors"
            >
              Walk your site with us
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="md:col-span-6">
          <div className="overflow-hidden">
            <img
              src={IMAGES.charleston}
              alt="Charleston, South Carolina commercial district"
              loading="lazy"
              className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
          <p className="mt-3 text-xs uppercase tracking-[0.2em] text-smoke">Charleston, SC — home base</p>
        </Reveal>
      </div>
    </section>
  );
}

function Divisions() {
  return (
    <section className="border-t border-ink/10 bg-[#F1F0ED] px-6 md:px-12 py-16 md:py-24" data-testid="divisions-section">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-smoke font-semibold mb-3">One company</p>
        <h2 className="font-display font-medium uppercase tracking-tighter text-3xl md:text-5xl mb-12">Two divisions.</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-ink/15">
        <Reveal>
          <Link
            to="/#services"
            data-testid="division-commercial-link"
            className="group block border-b border-r border-ink/15 p-8 md:p-12 bg-paper hover:bg-ink hover:text-paper transition-colors duration-300"
          >
            <span className="text-xs font-semibold text-accent">01</span>
            <h3 className="mt-6 font-display font-medium uppercase tracking-tight text-2xl md:text-3xl">Commercial Division</h3>
            <p className="mt-3 text-sm text-smoke group-hover:text-paper/60 transition-colors max-w-sm leading-relaxed">
              Upfits, new builds, renovations, and general construction for owners and developers.
            </p>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest">
              Explore <ArrowUpRight className="w-4 h-4" />
            </span>
          </Link>
        </Reveal>
        <Reveal delay={0.08}>
          <Link
            to="/residential"
            data-testid="division-residential-link"
            className="group block border-b border-r border-ink/15 p-8 md:p-12 bg-paper hover:bg-ink hover:text-paper transition-colors duration-300"
          >
            <span className="text-xs font-semibold text-accent">02</span>
            <h3 className="mt-6 font-display font-medium uppercase tracking-tight text-2xl md:text-3xl">Residential Division</h3>
            <p className="mt-3 text-sm text-smoke group-hover:text-paper/60 transition-colors max-w-sm leading-relaxed">
              Custom homes, renovations, and additions across the Charleston area.
            </p>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest">
              Explore <ArrowUpRight className="w-4 h-4" />
            </span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function CTASection() {
  return (
    <section className="bg-ink text-paper px-6 md:px-12 py-24 md:py-36 border-t border-ink" data-testid="cta-section">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-6">Next step</p>
        <h2 className="font-display font-medium uppercase tracking-tighter text-[10vw] md:text-[6vw] leading-[0.92] mb-12">
          Tell us about<br />the project.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/request-quote"
            data-testid="cta-quote-button"
            className="group inline-flex items-center gap-3 bg-paper text-ink font-semibold uppercase tracking-widest text-sm px-10 py-5 hover:bg-accent hover:text-paper transition-colors"
          >
            Request a Quote
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/request-site-visit"
            data-testid="cta-visit-button"
            className="group inline-flex items-center gap-3 border border-paper/50 text-paper font-semibold uppercase tracking-widest text-sm px-10 py-5 hover:border-accent hover:text-accent transition-colors"
          >
            Book a Site Visit
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = "Rogers American Construction | Charleston, SC General Contractor";
  }, []);
  return (
    <main>
      <Hero />
      <Marquee items={["Rogers American Construction", "Building Charleston for 25+ years the right way"]} />
      <About />
      <Services />
      <WhatWeDo />
      <Trust />
      <Charleston />
      <Divisions />
      <CTASection />
    </main>
  );
}
